import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "layout/Layout";
import Swal from 'sweetalert2';
import { useCreateMemberMutation } from 'api/consultancyApi';

const defaultState = {
  name: '',
  profile_photo: null,
  short_description: '',
  tagline: '',
  specialization_areas: '',
  biography: '',
  youtube_channel_title: '',
  youtube_video_url: '',
};

const CreateMember = () => {
  const [newMember, setNewMember] = useState(defaultState);
  const [errors, setErrors] = useState({});
  const [createMember, { isLoading }] = useCreateMemberMutation();
  const navigate = useNavigate();

  const handleNewChange = (field, value) => {
    setNewMember(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleNewFileChange = file => {
    setNewMember(prev => ({ ...prev, profile_photo: file }));
    if (errors.profile_photo) {
      setErrors((prev) => ({ ...prev, profile_photo: null }));
    }
  };

  const isValidYouTubeUrl = url => {
    if (!url) return true;
    const regex = /^(https?:\/\/)?(www\.)?(youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)[\w-]{11}/;
    return regex.test(url);
  };

  const validateForm = () => {
    const newErrors = {};
    const requiredFields = ['name', 'short_description', 'tagline', 'biography'];
    
    requiredFields.forEach(field => {
      if (!newMember[field]) {
        newErrors[field] = 'This field is required';
      }
    });

    if (!newMember.profile_photo) {
      newErrors.profile_photo = 'Profile photo is required';
    }

    if (newMember.youtube_video_url && !isValidYouTubeUrl(newMember.youtube_video_url)) {
      newErrors.youtube_video_url = 'Please enter a valid YouTube URL';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateSubmit = async e => {
    e.preventDefault();

    if (!validateForm()) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Please fill in all required fields correctly.',
        confirmButtonColor: '#d33',
      });
      return;
    }

    const formData = new FormData();
    formData.append('profile_photo', newMember.profile_photo);

    const specializationList = newMember.specialization_areas
      ? newMember.specialization_areas.split(',').map(s => s.trim()).filter(Boolean)
      : [];

    formData.append('specialization_areas', JSON.stringify(specializationList));

    ['name', 'short_description', 'tagline', 'biography', 'youtube_channel_title', 'youtube_video_url'].forEach(field => {
      if (newMember[field]) {
        formData.append(field, newMember[field].trim());
      }
    });

    try {
      await createMember(formData).unwrap();
      Swal.fire({
        icon: 'success',
        title: 'Member created successfully!',
        timer: 1500,
        showConfirmButton: false
      });
      setNewMember(defaultState);
      setTimeout(() => navigate('/consultancy'), 1600);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error?.data?.message || 'Something went wrong',
      });
    }
  };

  const InputField = ({ label, name, type = 'text', placeholder, isTextarea = false }) => (
    <div className="flex flex-col">
      <label className="mb-1 text-sm font-semibold text-gray-700">
        {label} {['name', 'short_description', 'tagline', 'biography'].includes(name) && <span className="text-red-500">*</span>}
      </label>
      {isTextarea ? (
        <textarea
          rows={4}
          value={newMember[name]}
          onChange={e => handleNewChange(name, e.target.value)}
          placeholder={placeholder}
          className={`border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 transition-all ${
            errors[name] ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200 focus:border-blue-500'
          }`}
        />
      ) : (
        <input
          type={type}
          value={newMember[name]}
          onChange={e => handleNewChange(name, e.target.value)}
          placeholder={placeholder}
          className={`border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 transition-all ${
            errors[name] ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200 focus:border-blue-500'
          }`}
        />
      )}
      {errors[name] && <span className="text-red-500 text-xs mt-1">{errors[name]}</span>}
    </div>
  );

  return (
    <Layout title="Manage Consultancy Team" tagline="Create your team member here">
      <div className="max-w-5xl mx-auto mt-10 md:mt-16 p-6 bg-white rounded-xl shadow-lg border border-gray-100 mb-20">
        <div className="border-b pb-4 mb-8 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Add New Member</h2>
            <p className="text-gray-500 text-sm mt-1">Fill in the details below to add a consultant to the team.</p>
          </div>
        </div>

        <form onSubmit={handleCreateSubmit} className="space-y-8">
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InputField label="Name" name="name" placeholder="e.g. Jane Doe" />
            <InputField label="Tagline" name="tagline" placeholder="e.g. Expert Nutritionist" />
            <InputField label="Short Description" name="short_description" placeholder="A brief overview..." />
            <InputField label="Specialization Areas" name="specialization_areas" placeholder="e.g. Diet, Fitness (Comma separated)" />
            <InputField label="YouTube Channel Title (Optional)" name="youtube_channel_title" placeholder="Channel Name" />
            <InputField label="YouTube Video URL (Optional)" name="youtube_video_url" placeholder="https://youtube.com/watch?v=..." />
          </div>

          <InputField label="Biography" name="biography" isTextarea={true} placeholder="Write the full biography here..." />

          <div className="flex flex-col">
            <label className="mb-2 text-sm font-semibold text-gray-700">Profile Photo <span className="text-red-500">*</span></label>
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
              <div className={`w-32 h-32 rounded-full overflow-hidden flex items-center justify-center shrink-0 border-4 shadow-sm ${errors.profile_photo ? 'border-red-400 bg-red-50' : 'border-gray-100 bg-gray-50'}`}>
                {newMember.profile_photo ? (
                  <img src={URL.createObjectURL(newMember.profile_photo)} alt="Preview" className="object-cover w-full h-full" />
                ) : (
                  <span className={`text-xs ${errors.profile_photo ? 'text-red-400' : 'text-gray-400'}`}>No Image</span>
                )}
              </div>
              <div className="w-full flex flex-col justify-center h-32">
                <div className={`border-2 border-dashed rounded-lg p-4 flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors h-full ${
                  errors.profile_photo ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'
                }`}>
                  <input type="file" id="profile_photo" accept="image/*" onChange={e => handleNewFileChange(e.target.files[0])} className="hidden" />
                  <label htmlFor="profile_photo" className="cursor-pointer flex flex-col items-center w-full h-full justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className={`h-8 w-8 mb-2 ${errors.profile_photo ? 'text-red-400' : 'text-blue-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1M12 12v9m0-9L9 15m3-3l3 3M12 3v9" />
                    </svg>
                    <span className="text-sm font-medium text-blue-600 hover:text-blue-700">
                      Click to upload image
                    </span>
                  </label>
                </div>
                {errors.profile_photo && <span className="text-red-500 text-xs mt-1 text-center">{errors.profile_photo}</span>}
              </div>
            </div>
          </div>

          <div className="pt-6 border-t flex justify-end gap-4 mt-8">
            <button
              type="button"
              onClick={() => navigate('/consultancy')}
              className="px-6 py-2.5 rounded-lg font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-8 py-2.5 rounded-lg font-bold text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 shadow-md transition-all"
            >
              {isLoading ? 'Creating...' : 'Create Member'}
            </button>
          </div>

        </form>
      </div>
    </Layout>
  );
};

export default CreateMember;
